import { useEffect, useState } from 'react';
import { Mail, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { reconciliationApi } from '@/api/reconciliation';
import { api } from '@/api/client';
import {
  buildDebtEmailDraft,
  formatEmailDateShort,
  formatEmailMoney,
  getLegalEntityLabel,
  type LegalEntity,
} from '@shared/types';
import type { UnpaidDocument } from './BalanceVisualization';

interface DebtEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientLegalName: string;
  legalEntity: LegalEntity;
  signerId?: string;
  unpaidDocuments: UnpaidDocument[];
  totalDebt: number;
}

export function DebtEmailDialog({
  open,
  onOpenChange,
  clientLegalName,
  legalEntity,
  signerId,
  unpaidDocuments,
  totalDebt,
}: DebtEmailDialogProps) {
  const companyName = getLegalEntityLabel(legalEntity, 'official');
  const draft = buildDebtEmailDraft({ clientLegalName, companyName, unpaidDocuments, totalDebt });

  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(draft.subject);
      setBody(draft.body);
    }
  }, [open, draft.subject, draft.body]);

  const handleSend = async () => {
    if (!email.trim()) {
      toast.error('Введите email получателя');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Некорректный email адрес');
      return;
    }

    if (unpaidDocuments.length === 0) {
      toast.error('Нет документов для отправки');
      return;
    }

    setIsSending(true);
    try {
      // Get PDFs for all unpaid documents
      const attachments: Array<{ base64: string; name: string }> = [];

      for (const doc of unpaidDocuments) {
        const base64 = await reconciliationApi.getDocumentPdfAsBase64(
          doc.invoiceId,
          doc.actId,
          doc.isPending,
          signerId
        );

        const docType = doc.isPending ? 'Счёт' : 'Акт';
        const fileName = `${docType}_${doc.invoiceNumber}_${formatEmailDateShort(doc.date)}.pdf`;
        attachments.push({ base64, name: fileName });
      }

      // Send email with multiple attachments
      await api.post('/email/send', {
        to: email.trim(),
        subject,
        body: body.replace(/\n/g, '<br>'),
        attachments: attachments.map(att => ({
          base64: att.base64,
          name: att.name,
        })),
      });

      toast.success('Email отправлен');
      onOpenChange(false);
    } catch (error: unknown) {
      console.error('Error sending email:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Ошибка отправки';
      toast.error('Ошибка отправки email', { description: errorMessage });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Отправить задолженность
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email получателя *</Label>
            <Input
              id="email"
              type="email"
              placeholder="example@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Тема письма</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Текст письма</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
            />
          </div>

          <div className="text-sm text-muted-foreground bg-muted p-3 rounded">
            <div className="font-medium mb-1">Вложения ({unpaidDocuments.length}):</div>
            <div className="space-y-0.5">
              {unpaidDocuments.map((doc, idx) => (
                <div key={doc.invoiceId || idx} className="text-xs">
                  {doc.isPending ? 'Счёт' : 'Акт'} №{doc.invoiceNumber} — {formatEmailMoney(doc.remainingAmount)} BYN
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 pt-4 border-t space-y-2">
          <Button
            className="w-full"
            onClick={handleSend}
            disabled={isSending || !email.trim()}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Отправка...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Отправить
              </>
            )}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Отмена
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
